import { fetchTokenAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex, widenUrlPattern } from "../urlPatternConversion";
import logo from "./logo.svg";

const REGIONS: Record<string, { ml: string; wx: string; token?: string }> = {
  "us-south": {
    ml: "https://us-south.ml.cloud.ibm.com",
    wx: "https://api.dataplatform.cloud.ibm.com",
  },
  "eu-de": {
    ml: "https://eu-de.ml.cloud.ibm.com",
    wx: "https://api.eu-de.dataplatform.cloud.ibm.com",
  },
  "eu-gb": {
    ml: "https://eu-gb.ml.cloud.ibm.com",
    wx: "https://api.eu-gb.dataplatform.cloud.ibm.com",
  },
  "jp-tok": {
    ml: "https://jp-tok.ml.cloud.ibm.com",
    wx: "https://api.jp-tok.dataplatform.cloud.ibm.com",
  },
  "au-syd": {
    ml: "https://au-syd.ml.cloud.ibm.com",
    wx: "https://api.au-syd.dai.cloud.ibm.com",
  },
  "ca-tor": {
    ml: "https://ca-tor.ml.cloud.ibm.com",
    wx: "https://api.ca-tor.dai.cloud.ibm.com",
  },
  "ap-south-1": {
    ml: "https://ap-south-1.aws.wxai.ibm.com",
    wx: "https://api.ap-south-1.aws.data.ibm.com",
    token: "https://ap-south-1.aws.data.ibm.com/api/rest/mcsp/apikeys/token",
  },
  "us-east-1": {
    ml: "https://us-east-1.aws.wxai.ibm.com",
    wx: "https://api.us-east-1.aws.data.ibm.com",
    token: "https://us-east-1.aws.data.ibm.com/api/rest/mcsp/apikeys/token",
  },
};

const endpoints = (region: string | undefined) =>
  REGIONS[region || "us-south"] ?? REGIONS["us-south"]!;

const connectionTestUrl = (region: string | undefined) =>
  `${endpoints(region).ml}/ml/v1/gpus?version=2024-03-14`;

export const ibmWatsonxAi = defineConnector({
  identifier: "ibmWatsonxAi",
  name: "IBM watsonx.ai",
  tagLine: "Build and run AI models",
  logo,
  protocol: "http",
  tags: ["ai", "engineering"],
  defaultUrlPattern: ({ region }) => {
    const { ml, wx } = endpoints(region);
    return widenUrlPattern(
      exactUrlToRegex(`${ml}/ml`),
      exactUrlToRegex(`${wx}/wx`)
    );
  },
  auth: (app) => {
    const { token } = endpoints(app.configuration.region);
    return fetchTokenAuth({
      request: token
        ? { url: token, jsonBody: { apikey: app.secret.apiKey } }
        : {
            url: "https://iam.cloud.ibm.com/identity/token",
            formBody: {
              grant_type: "urn:ibm:params:oauth:grant-type:apikey",
              apikey: app.secret.apiKey,
            },
          },
      extract: { from: "jsonPath", paths: ["access_token"] },
      apply: { kind: "header", scheme: "Bearer" },
    });
  },
  steps: [
    {
      title: "Choose a region",
      items: [
        {
          kind: "select",
          label: "Region",
          required: true,
          defaultValue: "us-south",
          target: { kind: "configuration", field: "region" },
          options: [
            { value: "us-south", label: "Dallas" },
            { value: "eu-de", label: "Frankfurt" },
            { value: "eu-gb", label: "London" },
            { value: "jp-tok", label: "Tokyo" },
            { value: "au-syd", label: "Sydney" },
            { value: "ca-tor", label: "Toronto" },
            { value: "ap-south-1", label: "Mumbai" },
            { value: "us-east-1", label: "Northern Virginia" },
          ],
        },
      ],
    },
    {
      title: "Add an API key",
      items: [
        {
          kind: "markdown",
          content:
            "Create an [IBM Cloud API key or AWS personal access key](https://www.ibm.com/docs/en/watsonx/saas?topic=resources-credentials-programmatic-access). [IBM Cloud service IDs](https://www.ibm.com/docs/en/watsonx/saas?topic=security-account) need the IAM Identity Operator role.",
        },
        {
          kind: "input",
          label: "API key",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ region }) => ({
      url: connectionTestUrl(region),
      expectedStatus: [200, 403],
    }),
  },
  publicEnvVars: ({ region }) => ({
    WATSONX_AI_URL: endpoints(region).ml,
    WATSONX_AI_WX_URL: `${endpoints(region).wx}/wx`,
  }),
  usageHint: () =>
    "Use process.env.WATSONX_AI_URL for model and deployment APIs, or process.env.WATSONX_AI_WX_URL for prompts, vector indexes, and agent tools. Include a version query parameter and a project_id or space_id when required. Authentication is automatic.",
});
